import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight, BookOpen, Search } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import { useAuth } from '../contexts/AuthContext';
import { GLOSSARY, GlossaryId, pageOfTerm } from '../content/glossary';
import { ACCESS_NOTES, NavGroup, NavItem, navForRole, navLabel, rolesWithAccess } from '../utils/navigation';
import { Role } from '../types/auth';
import { roleOf } from '../utils/roles';
import { scrollBehavior } from '../utils/motion';

const IDS = Object.keys(GLOSSARY) as GlossaryId[];
const ROLE_WORD: Record<Role, string> = { admin: 'admins', user: 'users', leader: 'leaders' };

interface GuidePageEntry {
  item: NavItem;
  /** The page's own "About this page" entry: it is the first paragraph, and the anchor of the page. */
  aboutId?: GlossaryId;
  terms: GlossaryId[];
}

// Who can open a page, in a few words.
export function whoCanOpen(path: string): string {
  const roles = rolesWithAccess(path);
  const note = ACCESS_NOTES[path];
  const base =
    roles.length === 3
      ? 'Open to everyone.'
      : roles.length === 1
        ? `${ROLE_WORD[roles[0]][0].toUpperCase()}${ROLE_WORD[roles[0]].slice(1)} only.`
        : `Open to ${roles.map((r) => ROLE_WORD[r]).join(' and ')}.`;
  return note ? `${base} ${note}` : base;
}

const has = (text: string | undefined, q: string) => (text ?? '').toLowerCase().includes(q);

// The pages a role can open, each with its words, narrowed by a search.
function build(role: Role, query: string): { group: NavGroup; pages: GuidePageEntry[] }[] {
  const q = query.trim().toLowerCase();
  return navForRole(role)
    .map(({ group, items }) => ({
      group,
      pages: items
        .map((item): GuidePageEntry | null => {
          const aboutId = IDS.find((id) => GLOSSARY[id].page === item.path);
          const all = IDS.filter((id) => !GLOSSARY[id].page && pageOfTerm(id) === item.path);
          if (!q) return { item, aboutId, terms: all };
          const pageHit = has(item.label, q) || has(item.purpose, q) || (aboutId ? has(GLOSSARY[aboutId].short, q) : false);
          const termHits = all.filter((id) => has(GLOSSARY[id].term, q) || has(GLOSSARY[id].short, q) || has(GLOSSARY[id].example, q));
          if (!pageHit && termHits.length === 0) return null;
          return { item, aboutId, terms: pageHit ? all : termHits };
        })
        .filter((p): p is GuidePageEntry => p !== null),
    }))
    .filter((g) => g.pages.length > 0);
}

// How to read Jarvis: every page a person can open, what it is for, who can open it, and what its words
// mean. It reads navigation.ts and glossary.ts, so there is no second copy to keep in step. The small (i)
// beside a word on a page links to the word here (/guide#flow.rework).
export default function GuidePage() {
  const { user } = useAuth();
  const role = roleOf(user);
  const { hash } = useLocation();
  const [query, setQuery] = useState('');
  const [flash, setFlash] = useState<string | null>(null);
  const sections = useMemo(() => build(role, query), [role, query]);
  const pageCount = sections.reduce((n, s) => n + s.pages.length, 0);
  const termCount = sections.reduce((n, s) => n + s.pages.reduce((m, p) => m + p.terms.length, 0), 0);

  // A link from a tip lands on the word and marks it for a moment.
  useEffect(() => {
    const id = decodeURIComponent(hash.replace(/^#/, ''));
    if (!id) return;
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ block: 'start', behavior: scrollBehavior() });
    el.focus({ preventScroll: true });
    setFlash(id);
    const timer = window.setTimeout(() => setFlash(null), 2500);
    return () => window.clearTimeout(timer);
  }, [hash]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100/60">
      <PageHeader title={navLabel('/guide')} subtitle="What each page is for, and what its words mean" icon={BookOpen} />
      <div className="p-4 sm:p-6">
        <div className="mx-auto max-w-4xl">
          <div className="mb-6 rounded-2xl bg-white p-5 shadow-sm ring-1 ring-gray-200/70">
            <h2 className="text-base font-semibold text-gray-900">How to read Jarvis</h2>
            <p className="mt-1 text-sm leading-relaxed text-gray-600">
              Jarvis reads Jira as it is now, so every number follows the tickets and the time people log. The pages are grouped by the question they answer. Press the small (i) beside a word on any page to read about it, and come here for the rest.
            </p>
            <label className="relative mt-4 block max-w-sm">
              <span className="sr-only">Search the guide</span>
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" aria-hidden />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search pages and words"
                className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm text-gray-800 placeholder:text-gray-500 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
              />
            </label>
            <p className="mt-2 text-xs text-gray-500" aria-live="polite">
              {query.trim()
                ? `${pageCount} ${pageCount === 1 ? 'page' : 'pages'} and ${termCount} ${termCount === 1 ? 'word' : 'words'} match.`
                : `${pageCount} pages and ${termCount} words you can open.`}
            </p>
          </div>

          {sections.length === 0 ? (
            <div className="rounded-2xl bg-white px-6 py-12 text-center text-sm text-gray-600 shadow-sm ring-1 ring-gray-200/70">
              <p>Nothing in the guide matches “{query.trim()}”.</p>
              <button type="button" onClick={() => setQuery('')} className="mt-3 rounded-lg px-3 py-1.5 text-sm font-medium text-blue-700 ring-1 ring-inset ring-blue-200 hover:bg-blue-50">
                Clear the search
              </button>
            </div>
          ) : (
            sections.map(({ group, pages }) => (
              <section key={group.id} aria-labelledby={`guide-group-${group.id}`} className="mb-8">
                <h2 id={`guide-group-${group.id}`} className="text-[0.6875rem] font-semibold uppercase tracking-wider text-gray-500">
                  {group.caption}
                </h2>
                <p className="mt-0.5 text-sm text-gray-600">{group.question}</p>
                <div className="mt-3 space-y-4">
                  {pages.map(({ item, aboutId, terms }) => {
                    const Icon = item.icon;
                    const anchor = aboutId ?? `page-${item.path.slice(1)}`;
                    return (
                      <article
                        key={item.path}
                        id={anchor}
                        aria-labelledby={`${anchor}-title`}
                        tabIndex={-1}
                        className={`scroll-mt-6 rounded-2xl bg-white p-5 shadow-sm ring-1 transition-shadow focus:outline-none motion-reduce:transition-none ${flash === anchor ? 'ring-2 ring-blue-400' : 'ring-gray-200/70'}`}
                      >
                        <header className="flex flex-wrap items-center justify-between gap-2">
                          <h3 id={`${anchor}-title`} className="flex items-center gap-2 text-base font-semibold text-gray-900">
                            <Icon className="h-4 w-4 text-blue-600" aria-hidden />
                            {item.label}
                          </h3>
                          <Link to={item.path} className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-200 hover:bg-blue-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500">
                            Open {item.label} <ArrowRight className="h-3 w-3" aria-hidden />
                          </Link>
                        </header>
                        <p className="mt-2 text-sm leading-relaxed text-gray-700">{aboutId ? GLOSSARY[aboutId].short : item.purpose}</p>
                        <p className="mt-1 text-xs text-gray-500">{whoCanOpen(item.path)}</p>
                        {terms.length > 0 && (
                          <dl className="mt-4 divide-y divide-gray-100 border-t border-gray-100">
                            {terms.map((id) => (
                              <div key={id} id={id} data-testid={`term:${id}`} tabIndex={-1} className={`scroll-mt-6 rounded-md py-3 transition-colors focus:outline-none motion-reduce:transition-none ${flash === id ? 'bg-blue-50' : ''}`}>
                                <dt className="text-sm font-semibold text-gray-900">{GLOSSARY[id].term}</dt>
                                <dd className="mt-0.5 text-sm leading-relaxed text-gray-600">
                                  {GLOSSARY[id].short}
                                  {GLOSSARY[id].example && <span className="mt-1 block text-xs text-gray-500">Example: {GLOSSARY[id].example}</span>}
                                </dd>
                              </div>
                            ))}
                          </dl>
                        )}
                      </article>
                    );
                  })}
                </div>
              </section>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
